"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.recurseZodObjectSchema = void 0;
const zod_1 = require("zod");
const utils_1 = require("./utils");
/**
 * Function goes through schema recursively and reconstructs it with ability to:
 * * modify individual schema field
 * * peek into individual schema field
 * * modify root and each recursive z.object
 *
 * @param schema - original zod schema
 * @param options - options depending on what needs to be done
 * @param options.peekFieldValue - check object fields that were recursively processed
 * @param options.adjustFieldValue - modify object fields that were recursively processed
 * @param options.adjustRecurseResult - modify entire z.object that was recursively processed
 *
 * @example
 * recurseZodObjectSchema(schema, {
 *     peekFieldValue: (fieldSchema) => {},             // peek
 *     adjustFieldValue: (fieldSchema) => fieldSchema,  // adjust single field
 *     adjustRecurseResult: (objSchema) => objSchema,   // adjust entire z.object
 * });
 * @example
 */
function recurseZodObjectSchema(schema, options) {
    return recurseZodObjectSchemaInternal(schema, options, { path: [] });
}
exports.recurseZodObjectSchema = recurseZodObjectSchema;
function recurseZodObjectSchemaInternal(schema, options, optionsInternal) {
    var _a, _b, _c, _d, _e;
    const shapeNew = {};
    for (const key in schema.shape) {
        const path = [...optionsInternal.path, key];
        const fieldDataOriginal = getSchemaForField(schema.shape[key]);
        const fieldSchemaOriginal = fieldDataOriginal.fieldSchema;
        if (fieldSchemaOriginal instanceof zod_1.z.ZodArray) {
            const arrayElementOriginal = getSchemaForField(fieldSchemaOriginal._def.type);
            if (arrayElementOriginal.fieldSchema instanceof zod_1.z.ZodObject) {
                const arrayElementSchema = recurseZodObjectSchemaInternal(arrayElementOriginal.fieldSchema, options, { key, path });
                shapeNew[key] = redecorateArray(zod_1.z.array(redecorate(arrayElementSchema, arrayElementOriginal)), fieldSchemaOriginal);
            }
            else {
                shapeNew[key] = fieldSchemaOriginal;
            }
        }
        else if (fieldSchemaOriginal instanceof zod_1.z.ZodRecord) {
            const recordElementOriginal = getSchemaForField(fieldSchemaOriginal.element);
            if (recordElementOriginal.fieldSchema instanceof zod_1.z.ZodObject) {
                const recordElementSchema = recurseZodObjectSchemaInternal(recordElementOriginal.fieldSchema, options, { key, path });
                shapeNew[key] = zod_1.z.record(fieldSchemaOriginal.keySchema, redecorate(recordElementSchema, recordElementOriginal));
            }
            else {
                shapeNew[key] = fieldSchemaOriginal;
            }
        }
        else if (fieldSchemaOriginal instanceof zod_1.z.ZodObject) {
            shapeNew[key] = recurseZodObjectSchemaInternal(fieldSchemaOriginal, options, { key, path });
        }
        else if (fieldSchemaOriginal instanceof zod_1.z.ZodIntersection) {
            throw new Error('intersection via .and() or .intersection() are not supported, use .merge() instead');
        }
        else {
            shapeNew[key] = fieldSchemaOriginal;
        }
        // field callbacks
        (_a = options === null || options === void 0 ? void 0 : options.peekFieldValue) === null || _a === void 0 ? void 0 : _a.call(options, shapeNew[key], { key, path });
        shapeNew[key] = (_c = (_b = options === null || options === void 0 ? void 0 : options.adjustFieldValue) === null || _b === void 0 ? void 0 : _b.call(options, shapeNew[key], { key, path })) !== null && _c !== void 0 ? _c : shapeNew[key];
        shapeNew[key] = redecorate(shapeNew[key], fieldDataOriginal);
    }
    const result = zod_1.z.object(shapeNew);
    return ((_e = (_d = options.adjustRecurseResult) === null || _d === void 0 ? void 0 : _d.call(options, result, { key: optionsInternal.key, path: optionsInternal.path })) !== null && _e !== void 0 ? _e : result);
}
function redecorate(schemaNew, decoratorsOriginal) {
    let schema = schemaNew;
    if (decoratorsOriginal.isOptional) {
        schema = schema.optional();
    }
    if (decoratorsOriginal.isNullable) {
        schema = schema.nullable();
    }
    return schema;
}
function redecorateArray(arraySchemaNew, arraySchemaOriginal) {
    var _a, _b, _c;
    // redecorate - array specific
    if ((0, utils_1.isNumber)((_a = arraySchemaOriginal._def.maxLength) === null || _a === void 0 ? void 0 : _a.value)) {
        arraySchemaNew = arraySchemaNew.max(arraySchemaOriginal._def.maxLength.value, arraySchemaOriginal._def.maxLength.message);
    }
    if ((0, utils_1.isNumber)((_b = arraySchemaOriginal._def.minLength) === null || _b === void 0 ? void 0 : _b.value)) {
        arraySchemaNew = arraySchemaNew.min(arraySchemaOriginal._def.minLength.value, arraySchemaOriginal._def.minLength.message);
    }
    if ((0, utils_1.isNumber)((_c = arraySchemaOriginal._def.exactLength) === null || _c === void 0 ? void 0 : _c.value)) {
        arraySchemaNew = arraySchemaNew.length(arraySchemaOriginal._def.exactLength.value, arraySchemaOriginal._def.exactLength.message);
    }
    return arraySchemaNew;
}
function getSchemaForField(shape) {
    const resultDefault = {
        fieldSchema: shape,
        isOptional: false,
        isNullable: false,
    };
    if (shape instanceof zod_1.z.ZodOptional) {
        return shape._def.innerType instanceof zod_1.z.ZodNullable
            ? {
                fieldSchema: shape._def.innerType._def.innerType,
                isOptional: true,
                isNullable: true,
            }
            : Object.assign(Object.assign({}, resultDefault), { fieldSchema: shape._def.innerType, isOptional: true });
    }
    if (shape instanceof zod_1.z.ZodNullable) {
        return shape._def.innerType instanceof zod_1.z.ZodOptional
            ? {
                fieldSchema: shape._def.innerType._def.innerType,
                isOptional: true,
                isNullable: true,
            }
            : Object.assign(Object.assign({}, resultDefault), { fieldSchema: shape._def.innerType, isNullable: true });
    }
    return resultDefault;
}
